import { useEffect, useRef, useState } from 'react';
import {
  isSafeSystemMessageHref,
  isSafeSystemMessageImageUrl,
  sanitizeCssLength,
  SYSTEM_MESSAGE_IMAGE_LIGHTBOX_ATTR,
  SYSTEM_MESSAGE_IMAGE_LIGHTBOX_SRC_ATTR,
} from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';

type HtmlEditorProps = {
  id: string;
  label: string;
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
};

type EditorMode = 'visual' | 'html';

const TOOL_BTN_CLASS =
  'rounded border border-slate-600 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-800 disabled:opacity-50';

const toolBtnClass = (active: boolean): string =>
  `${TOOL_BTN_CLASS}${active ? ' bg-slate-700' : ''}`;

const EDITOR_SURFACE_CLASS =
  'min-h-28 w-full rounded-md border border-slate-600 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60';

const htmlPreservesLightboxMarkup = (previous: string, next: string): boolean => {
  const attrs = [SYSTEM_MESSAGE_IMAGE_LIGHTBOX_ATTR, SYSTEM_MESSAGE_IMAGE_LIGHTBOX_SRC_ATTR];
  return attrs.every((attr) => !previous.includes(attr) || next.includes(attr));
};

/** Admin system message detail: HTML source is canonical; visual is a preview/edit surface. */
export const HtmlEditor = ({ id, label, value, onChange, disabled = false }: HtmlEditorProps) => {
  const { t } = useLanguage();
  const visualRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingTextareaSelect = useRef<{ start: number; end: number } | null>(null);
  const committedHtmlRef = useRef(value);
  const skipVisualInputRef = useRef(false);
  const [mode, setMode] = useState<EditorMode>('html');

  useEffect(() => {
    committedHtmlRef.current = value;
  }, [value]);

  useEffect(() => {
    if (mode !== 'visual') {
      return;
    }
    const el = visualRef.current;
    if (!el) {
      return;
    }
    if (document.activeElement === el) {
      return;
    }
    if (el.innerHTML !== (value || '')) {
      el.innerHTML = value || '';
    }
  }, [value, mode]);

  useEffect(() => {
    const ta = textareaRef.current;
    const pending = pendingTextareaSelect.current;
    if (mode !== 'html' || !ta || !pending) {
      return;
    }
    pendingTextareaSelect.current = null;
    ta.focus();
    ta.setSelectionRange(pending.start, pending.end);
  }, [value, mode]);

  const escapeHtmlAttr = (text: string): string =>
    text.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const escapeHtmlText = (text: string): string =>
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const insertAtTextareaCursor = (snippet: string): void => {
    const current = value || '';
    const ta = textareaRef.current;
    const start = ta?.selectionStart ?? current.length;
    const end = ta?.selectionEnd ?? current.length;
    const next = `${current.slice(0, start)}${snippet}${current.slice(end)}`;
    const caret = start + snippet.length;
    pendingTextareaSelect.current = { start: caret, end: caret };
    onChange(next);
  };

  const wrapTextareaSelection = (before: string, after: string): void => {
    const current = value || '';
    const ta = textareaRef.current;
    const start = ta?.selectionStart ?? current.length;
    const end = ta?.selectionEnd ?? current.length;
    const selected = current.slice(start, end);
    const snippet = `${before}${selected}${after}`;
    const next = `${current.slice(0, start)}${snippet}${current.slice(end)}`;
    const innerStart = start + before.length;
    pendingTextareaSelect.current = { start: innerStart, end: innerStart + selected.length };
    onChange(next);
  };

  const commitVisualHtml = (html: string): void => {
    if (!htmlPreservesLightboxMarkup(committedHtmlRef.current || '', html)) {
      const el = visualRef.current;
      if (el) {
        el.innerHTML = committedHtmlRef.current || '';
      }
      return;
    }
    committedHtmlRef.current = html;
    onChange(html);
  };

  const insertSnippet = (snippet: string): void => {
    if (mode === 'html') {
      insertAtTextareaCursor(snippet);
      return;
    }
    const next = `${value || ''}${snippet}`;
    committedHtmlRef.current = next;
    skipVisualInputRef.current = true;
    onChange(next);
    const el = visualRef.current;
    if (el) {
      el.innerHTML = next;
    }
    queueMicrotask(() => {
      skipVisualInputRef.current = false;
    });
  };

  const handleCommand = (command: string, commandValue?: string): void => {
    if (disabled) {
      return;
    }
    if (mode === 'html') {
      if (command === 'bold') {
        wrapTextareaSelection('<strong>', '</strong>');
        return;
      }
      if (command === 'italic') {
        wrapTextareaSelection('<em>', '</em>');
        return;
      }
      if (command === 'insertUnorderedList') {
        wrapTextareaSelection('<ul><li>', '</li></ul>');
        return;
      }
      if (command === 'insertOrderedList') {
        wrapTextareaSelection('<ol><li>', '</li></ol>');
        return;
      }
      if (command === 'createLink' && commandValue) {
        wrapTextareaSelection(`<a href="${escapeHtmlAttr(commandValue)}">`, '</a>');
      }
      return;
    }
    document.execCommand(command, false, commandValue);
    if (visualRef.current) {
      commitVisualHtml(visualRef.current.innerHTML);
    }
  };

  const handleLink = (): void => {
    if (disabled) {
      return;
    }
    const url = window.prompt(t('admin.systemMessage.linkPrompt'), 'https://');
    if (!url) {
      return;
    }
    handleCommand('createLink', url);
  };

  const promptHttpsImageUrl = (): string | null => {
    const url = window.prompt(t('admin.systemMessage.imageUrlPrompt'), 'https://')?.trim() ?? '';
    if (url === '') {
      return null;
    }
    if (!isSafeSystemMessageImageUrl(url)) {
      window.alert(t('admin.systemMessage.imageUrlInvalid'));
      return null;
    }
    return url;
  };

  const wrapLightboxHtml = (url: string, innerHtml: string): string =>
    `<span role="button" tabindex="0" ${SYSTEM_MESSAGE_IMAGE_LIGHTBOX_ATTR}="1" ${SYSTEM_MESSAGE_IMAGE_LIGHTBOX_SRC_ATTR}="${escapeHtmlAttr(url)}">${innerHtml}</span>`;

  const promptOptionalClickUrl = (): { ok: true; href: string | null } | { ok: false } => {
    const raw = window.prompt(t('admin.systemMessage.imageClickUrlPrompt'), '');
    if (raw == null) {
      return { ok: true, href: null };
    }
    const trimmed = raw.trim();
    if (trimmed === '' || trimmed === 'https://') {
      return { ok: true, href: null };
    }
    if (!isSafeSystemMessageHref(trimmed)) {
      window.alert(t('admin.systemMessage.imageClickUrlInvalid'));
      return { ok: false };
    }
    return { ok: true, href: trimmed };
  };

  const handleInsertImage = (): void => {
    if (disabled) {
      return;
    }
    const url = promptHttpsImageUrl();
    if (!url) {
      return;
    }
    const maxWidth = sanitizeCssLength(
      window.prompt(t('admin.systemMessage.imageMaxWidthPrompt'), '') ?? '',
    );
    const maxHeight = sanitizeCssLength(
      window.prompt(t('admin.systemMessage.imageMaxHeightPrompt'), '') ?? '',
    );
    const clickUrlResult = promptOptionalClickUrl();
    if (!clickUrlResult.ok) {
      return;
    }
    const styleParts = [
      ...(maxWidth ? [`max-width: ${maxWidth}`] : []),
      ...(maxHeight ? [`max-height: ${maxHeight}`] : []),
    ];
    const styleAttr = styleParts.length > 0 ? ` style="${escapeHtmlAttr(styleParts.join('; '))}"` : '';
    const imgHtml = `<img src="${escapeHtmlAttr(url)}" alt=""${styleAttr} />`;
    if (clickUrlResult.href) {
      insertSnippet(`<a href="${escapeHtmlAttr(clickUrlResult.href)}">${imgHtml}</a>`);
      return;
    }
    insertSnippet(wrapLightboxHtml(url, imgHtml));
  };

  const handleInsertImageLink = (): void => {
    if (disabled) {
      return;
    }
    const url = promptHttpsImageUrl();
    if (!url) {
      return;
    }
    const selected =
      mode === 'html'
        ? (value || '').slice(textareaRef.current?.selectionStart ?? 0, textareaRef.current?.selectionEnd ?? 0)
        : (document.getSelection()?.toString() ?? '');
    const prompted =
      selected.trim() !== ''
        ? selected
        : (window.prompt(t('admin.systemMessage.imageLinkTextPrompt'), '') ?? '').trim();
    const text = prompted !== '' ? prompted : url;
    insertSnippet(wrapLightboxHtml(url, escapeHtmlText(text)));
  };

  const handleSwitchToVisual = (): void => {
    setMode('visual');
  };

  const handleSwitchToHtml = (): void => {
    if (mode === 'visual' && visualRef.current && document.activeElement === visualRef.current) {
      commitVisualHtml(visualRef.current.innerHTML);
    }
    setMode('html');
  };

  const htmlSourceId = `${id}-html-source`;

  return (
    <div>
      <label htmlFor={mode === 'html' ? htmlSourceId : id} className="mb-1 block text-xs font-medium text-slate-300">
        {label}
      </label>
      <div className="mb-1 flex flex-wrap items-center gap-1">
        <button
          type="button"
          className={toolBtnClass(mode === 'visual')}
          onClick={handleSwitchToVisual}
          disabled={disabled}
          aria-pressed={mode === 'visual'}
          aria-label={t('admin.systemMessage.editorModeVisual')}
          tabIndex={0}
        >
          {t('admin.systemMessage.editorModeVisual')}
        </button>
        <button
          type="button"
          className={toolBtnClass(mode === 'html')}
          onClick={handleSwitchToHtml}
          disabled={disabled}
          aria-pressed={mode === 'html'}
          aria-label={t('admin.systemMessage.editorModeHtml')}
          tabIndex={0}
        >
          {t('admin.systemMessage.editorModeHtml')}
        </button>
        <span className="mx-1 h-4 w-px bg-slate-600" aria-hidden />
        <button
          type="button"
          className={TOOL_BTN_CLASS}
          onClick={() => handleCommand('bold')}
          disabled={disabled}
          aria-label={t('admin.systemMessage.editorBold')}
          tabIndex={0}
        >
          B
        </button>
        <button
          type="button"
          className={`${TOOL_BTN_CLASS} italic`}
          onClick={() => handleCommand('italic')}
          disabled={disabled}
          aria-label={t('admin.systemMessage.editorItalic')}
          tabIndex={0}
        >
          I
        </button>
        <button
          type="button"
          className={TOOL_BTN_CLASS}
          onClick={() => handleCommand('insertUnorderedList')}
          disabled={disabled}
          aria-label={t('admin.systemMessage.editorUl')}
          tabIndex={0}
        >
          UL
        </button>
        <button
          type="button"
          className={TOOL_BTN_CLASS}
          onClick={() => handleCommand('insertOrderedList')}
          disabled={disabled}
          aria-label={t('admin.systemMessage.editorOl')}
          tabIndex={0}
        >
          OL
        </button>
        <button
          type="button"
          className={TOOL_BTN_CLASS}
          onClick={handleLink}
          disabled={disabled}
          aria-label={t('admin.systemMessage.editorLink')}
          tabIndex={0}
        >
          Link
        </button>
        <button
          type="button"
          className={TOOL_BTN_CLASS}
          onClick={handleInsertImage}
          disabled={disabled}
          aria-label={t('admin.systemMessage.editorImage')}
          tabIndex={0}
        >
          Img
        </button>
        <button
          type="button"
          className={TOOL_BTN_CLASS}
          onClick={handleInsertImageLink}
          disabled={disabled}
          aria-label={t('admin.systemMessage.editorImageLink')}
          tabIndex={0}
        >
          Img text
        </button>
      </div>
      {mode === 'html' ? (
        <textarea
          id={htmlSourceId}
          ref={textareaRef}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          spellCheck={false}
          aria-label={label}
          tabIndex={0}
          className={`${EDITOR_SURFACE_CLASS} min-h-40 resize-y font-mono text-xs leading-5`}
        />
      ) : (
        <div
          id={id}
          ref={visualRef}
          contentEditable={!disabled}
          role="textbox"
          aria-multiline="true"
          aria-label={label}
          tabIndex={0}
          className={EDITOR_SURFACE_CLASS}
          onInput={() => {
            if (skipVisualInputRef.current || !visualRef.current) {
              return;
            }
            const html = visualRef.current.innerHTML;
            if (html === committedHtmlRef.current) {
              return;
            }
            commitVisualHtml(html);
          }}
          suppressContentEditableWarning
        />
      )}
    </div>
  );
};
